import { type FC, type RefObject, useEffect, useRef } from "react";

import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  PASTE_COMMAND,
} from "lexical";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";

import { type ParagraphRun } from "./paragraph_editor_runs";
import { readRuns } from "./paragraph_editor_state";

// One paragraph: Enter is his file's (Shift+Enter breaks a line), paste is
// plain, Escape leaves it unsaved, and a click outside (`outside`) or going
// inactive saves it. Not on unmount: React's dev check remounts it on opening.
export const ParagraphRulesPlugin: FC<{
  caret: number;
  outside: ReadonlyArray<Document>;
  active: boolean;
  container: RefObject<HTMLDivElement | null>;
  // Set once he's left it, by Done, Escape or here, so it's saved once.
  closed: RefObject<boolean>;
  onDone(runs: ParagraphRun[]): void;
  onCancel(): void;
}> = ({ caret, outside, active, container, closed, onDone, onCancel }) => {
  const [editor] = useLexicalComposerContext();
  const latest = useRef({ onDone, onCancel });
  latest.current = { onDone, onCancel };

  useEffect(() => {
    editor.update(
      () => {
        let remaining = caret;
        for (const node of $getRoot().getAllTextNodes()) {
          const size = node.getTextContentSize();
          if (remaining <= size) {
            // Typing goes on in the run's format, as in Word, its end included.
            node.select(remaining, remaining).format = node.getFormat();
            return;
          }
          remaining -= size;
        }
        $getRoot().selectEnd();
      },
      { discrete: true },
    );
    // Out of the Report's frame, where his click left the focus.
    editor.getRootElement()?.focus({ preventScroll: true });
  }, [editor, caret]);

  useEffect(() => {
    const leave = () => {
      if (closed.current) return;
      closed.current = true;
      latest.current.onDone(readRuns(editor));
    };
    if (!active) leave();
    const click = (event: MouseEvent) => {
      if (container.current?.contains(event.target as Node)) return;
      leave();
    };
    outside.forEach((doc) => doc.addEventListener("mousedown", click));
    return () =>
      outside.forEach((doc) => doc.removeEventListener("mousedown", click));
  }, [editor, active, outside, container, closed]);

  useEffect(
    () =>
      mergeRegister(
        editor.registerCommand(
          KEY_ENTER_COMMAND,
          (event) => {
            if (event?.shiftKey) return false;
            event?.preventDefault();
            return true;
          },
          COMMAND_PRIORITY_HIGH,
        ),
        editor.registerCommand(
          PASTE_COMMAND,
          (event) => {
            if (!(event instanceof ClipboardEvent)) return false;
            const text = event.clipboardData?.getData("text/plain") ?? "";
            event.preventDefault();
            editor.update(() => {
              const selection = $getSelection();
              if ($isRangeSelection(selection)) {
                selection.insertRawText(text.replace(/\r\n?/g, "\n"));
              }
            });
            return true;
          },
          COMMAND_PRIORITY_HIGH,
        ),
        editor.registerCommand(
          KEY_ESCAPE_COMMAND,
          () => {
            closed.current = true;
            latest.current.onCancel();
            return true;
          },
          COMMAND_PRIORITY_HIGH,
        ),
      ),
    [editor, closed],
  );
  return null;
};
